import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
	TrackByFunction,
} from '@angular/core';
import {
	AccordionItemComponent,
	DatalayerService,
	HistoryService,
	ModalSize,
	NotificationType,
	RoutedModalBaseComponent,
	slugify,
	TrackingEvent,
	ButtonComponent,
	TrackEventDirective,
	SvgIconComponent,
	AlertComponent,
	AccordionComponent,
	SlottedModalComponent,
	WappleLoadingComponent,
	ImageZoomDirective,
	FormattedDatePipe,
	CapitalizeSpecificWordsPipe,
} from '@woolworthsnz/styleguide';
import { MODAL_ROUTES, STORE_LOCATOR_PATH } from '@woolworthsnz/shop';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';

import {
	buildDirectionsUrl,
	getTradingHourAsDate,
	mapState,
	Site,
	SiteDetail,
	SiteLocation,
	StoresService,
	TimeRequest,
} from '@woolworthsnz/store-locator';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ProductService, ProductImageComponent } from '@woolworthsnz/product';
import { FulfilmentStoreStockLevelResponse, ProductStoresStockLevelResponse } from '@woolworthsnz/trader-api';
import { FulfilmentService } from '@woolworthsnz/fulfilment';
import StockLevelEnum = FulfilmentStoreStockLevelResponse.StockLevelEnum;
import { ProductStockLevelIndicatorComponent } from '../product-stock-level-indicator/product-stock-level-indicator.component';
import { NgIf, NgFor, TitleCasePipe } from '@angular/common';

export interface SiteDetailWithStockLevel extends SiteDetail {
	id: number;
	stockLevel: FulfilmentStoreStockLevelResponse.StockLevelEnum;
	pickupAddressId?: number;
	fulfilmentStoreId?: number;
	openingTime?: Date;
	closingTime?: Date;
	nextOpeningTime?: Date;
	isOpen?: boolean;
	expectedOpeningTime?: Date;
	directionsUrl?: string;
	storeInfoUrl?: string;
}

@Component({
	selector: 'wnz-product-store-stock-level-modal',
	templateUrl: './product-store-stock-level-modal.component.html',
	styleUrls: ['./product-store-stock-level-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		SlottedModalComponent,
		ProductImageComponent,
		ImageZoomDirective,
		NgIf,
		WappleLoadingComponent,
		AccordionComponent,
		NgFor,
		AccordionItemComponent,
		ProductStockLevelIndicatorComponent,
		SvgIconComponent,
		ButtonComponent,
		TrackEventDirective,
		AlertComponent,
		TitleCasePipe,
		CapitalizeSpecificWordsPipe,
		FormattedDatePipe,
	],
})
export class ProductStoreStockLevelModalComponent extends RoutedModalBaseComponent implements OnInit, OnDestroy {
	@Output() detailsClick = new EventEmitter();

	product: any;
	currentMainImageIndex = 0;
	currentLocationError = false;
	searchResults?: SiteDetailWithStockLevel[];
	time = TimeRequest;
	notificationType = NotificationType;
	trackingEvent = TrackingEvent;
	loading = true;

	modalSize = ModalSize;

	readonly storeCountMax = 12;
	private readonly storeCountMin = 3;
	private requestStoreCount = 3;
	private expectedStoreCount = 3;

	private location?: GeolocationPosition;
	private loadStoresSubject = new Subject<number>();
	private loadStoresSubscription: Subscription;
	private searchResultsSubscription: Subscription;
	private productService: ProductService;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		protected router: Router,
		protected historyService: HistoryService,
		private fulfilmentService: FulfilmentService,
		private storesService: StoresService,
		private dataLayerService: DatalayerService,
		private cdr: ChangeDetectorRef,
		protected activatedRoute: ActivatedRoute
	) {
		super(router, activatedRoute, historyService);

		const state = this.router.getCurrentNavigation()?.extras.state;

		if (state) {
			const { product, productService } = state;
			this.productService = productService;
			this.product = product;
		}
	}

	get mainImage(): string {
		return (this.product?.images || [])[this.currentMainImageIndex];
	}

	ngOnInit(): void {
		this.loadStoresSubscription = this.loadStoresSubject
			.pipe(takeUntil(this.destroyed$))
			.subscribe((numberOfStores: number) => {
				if (this.location) {
					this.searchStoresByLocation(this.location, numberOfStores);
				}
			});

		this.handleCurrentLocation();
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		if (this.loadStoresSubscription) {
			this.loadStoresSubscription.unsubscribe();
		}

		if (this.searchResultsSubscription) {
			this.searchResultsSubscription.unsubscribe();
		}
	}

	handleCurrentLocation(): void {
		if (!navigator.geolocation) {
			return this.handleGetLocationFailure();
		}

		this.currentLocationError = false;

		navigator.geolocation.getCurrentPosition(
			(location: GeolocationPosition) => {
				this.location = location;
				this.loadStoresSubject.next(this.storeCountMin);
			},
			() => this.handleGetLocationFailure()
		);
	}

	searchStoresByLocation(location: GeolocationPosition, storeCount: number): void {
		this.loading = true;

		this.searchResultsSubscription = this.storesService
			.searchStoresDetailed(location.coords.latitude, location.coords.longitude, storeCount)
			.pipe(
				takeUntil(this.destroyed$),
				map((siteLocation: SiteLocation) => this.getStoresInfo(siteLocation)),
				switchMap((siteDetailsWithStockLevel: SiteDetailWithStockLevel[]) => {
					if (siteDetailsWithStockLevel && siteDetailsWithStockLevel.length) {
						// We need to get the list of fulfilment store ids which is configured in the "extra1" field of the store info
						// in store locator
						const fulfilmentStoreIds: number[] = this.getFulfilmentStoreIds(siteDetailsWithStockLevel);

						// Load the stock levels from the Trader API and set store stock level value
						return this.productService
							.getProductStoresStockLevels(this.product.sku, fulfilmentStoreIds)
							.pipe(
								map((stockLevelResponse: ProductStoresStockLevelResponse) => {
									// Set the stock level indicator for each store locator store
									stockLevelResponse.fulfilmentStoreStockLevels?.forEach((storeStockLevel) => {
										const siteDetail = siteDetailsWithStockLevel.find(
											(c) => c.site?.extra1 === storeStockLevel.fulfilmentStoreId?.toString()
										);
										if (siteDetail) {
											siteDetail.stockLevel =
												storeStockLevel.stockLevel ??
												FulfilmentStoreStockLevelResponse.StockLevelEnum.Unknown;
											siteDetail.pickupAddressId = storeStockLevel.pickupAddress?.addressId;
											siteDetail.fulfilmentStoreId = storeStockLevel.fulfilmentStoreId;
										}
									});

									const filteredResult = siteDetailsWithStockLevel.filter(
										(c) => c.stockLevel !== StockLevelEnum.Unknown
									);
									if (filteredResult.length > this.storeCountMax) {
										return filteredResult.slice(0, this.storeCountMax);
									}

									return filteredResult;
								})
							);
					} else {
						return of(siteDetailsWithStockLevel);
					}
				}),
				tap((siteDetailsWithStockLevel: SiteDetailWithStockLevel[]) => {
					if (!this.searchResults) {
						this.searchResults = siteDetailsWithStockLevel;
					} else {
						siteDetailsWithStockLevel.forEach((store) => {
							if (this.searchResults) {
								const existingIndex = this.searchResults?.findIndex((s) => s.id === store.id);
								if (existingIndex === -1) {
									this.searchResults.push(store);
								}
							}
						});
					}

					// If not all the stores returned from store locator were Trader  pickup fulfilment stores, load more stores from store locator
					if (this.requestStoreCount < 24 && this.searchResults.length < this.expectedStoreCount) {
						const diff = this.expectedStoreCount - this.searchResults.length;
						this.requestStoreCount += diff;
						return this.loadStoresSubject.next(this.requestStoreCount);
					}

					this.loading = false;
				}),
				catchError((err) => {
					this.loading = false;
					this.cdr.markForCheck();
					return of(err);
				})
			)
			.subscribe(() => {
				this.cdr.markForCheck();
			});
	}

	handleGetLocationFailure(): void {
		this.loading = false;
		this.currentLocationError = true;
		this.cdr.markForCheck();
	}

	trackByFn: TrackByFunction<SiteDetail> = (_, item) => item.site?.id;

	getViewStoreInfoUrl(storeSite: Site): string {
		return (
			'/' +
			STORE_LOCATOR_PATH +
			'/' +
			storeSite.id +
			'/' +
			slugify(storeSite.suburb || '') +
			'/' +
			slugify(storeSite.name || '') +
			'?lat=' +
			storeSite.latitude +
			'&long=' +
			storeSite.longitude +
			'&q=&breadcrumb=false'
		);
	}

	isOpen(openingTime: Date, closingTime: Date): boolean {
		// TODO: CO-9550 remove dayjs() and use server time offset
		const now = dayjs();
		return now.isSameOrAfter(openingTime) && now.isBefore(closingTime);
	}

	expectedOpeningTime(openingTime: Date, closingTime: Date, nextOpeningTime: Date): Date {
		// if the store will be open later today return the opening time
		if (dayjs().isBefore(openingTime) && dayjs(openingTime).isBefore(closingTime)) {
			return openingTime;
		}

		return nextOpeningTime;
	}

	mapStateToString(site: Site): string {
		return mapState(site);
	}

	onLoadMoreStoresClicked(): void {
		// Increase both the expected store count and the requested store count by 3
		this.expectedStoreCount += this.storeCountMin;

		// The request store count can be increased by the api response subscriber if not all stores
		// returned from store locator are pickup fulfilment stores
		this.requestStoreCount += this.storeCountMin;
		this.loadStoresSubject.next(this.requestStoreCount);
	}

	private getStoresInfo(siteLocation: SiteLocation): SiteDetailWithStockLevel[] {
		const siteDetailsWithStockLevel: SiteDetailWithStockLevel[] = [];

		if (siteLocation && siteLocation.siteDetail) {
			siteLocation.siteDetail.forEach((siteDetail: SiteDetail) => {
				const siteWithStockLevel: SiteDetailWithStockLevel = {
					...siteDetail,
					id: siteDetail.site?.id ?? 0,
					stockLevel: StockLevelEnum.Unknown,
				};

				siteWithStockLevel.openingTime = getTradingHourAsDate(siteDetail.tradingHours ?? [], this.time.opening);
				siteWithStockLevel.closingTime = getTradingHourAsDate(siteDetail.tradingHours ?? [], this.time.closing);
				siteWithStockLevel.nextOpeningTime = getTradingHourAsDate(
					siteDetail.tradingHours ?? [],
					this.time.nextOpening
				);

				if (siteWithStockLevel.openingTime && siteWithStockLevel.closingTime) {
					siteWithStockLevel.isOpen = this.isOpen(
						siteWithStockLevel.openingTime,
						siteWithStockLevel.closingTime
					);

					if (siteWithStockLevel.nextOpeningTime) {
						siteWithStockLevel.expectedOpeningTime = this.expectedOpeningTime(
							siteWithStockLevel.openingTime,
							siteWithStockLevel.closingTime,
							siteWithStockLevel.nextOpeningTime
						);
					}
				}

				// Build router links for directions & store info
				if (siteDetail.site) {
					siteWithStockLevel.directionsUrl = buildDirectionsUrl(
						siteDetail.site.latitude,
						siteDetail.site.longitude
					);

					siteWithStockLevel.storeInfoUrl = this.getViewStoreInfoUrl(siteDetail.site);
				}

				siteDetailsWithStockLevel.push(siteWithStockLevel);
			});
		}

		return siteDetailsWithStockLevel;
	}

	private getFulfilmentStoreIds(siteDetails: SiteDetail[]): number[] {
		return siteDetails
			.filter((c) => c.site?.extra1 !== undefined && c.site.extra1 !== 'null')
			.map((s: SiteDetail) => {
				if (s.site?.extra1) {
					return +s.site.extra1.trim();
				}

				return 0;
			});
	}

	addToTrolley(): void {
		this.productService.addMinimumProductQuantity();
	}

	changePickupStoreAndAddToTrolley(
		physicalStoreId: number,
		fulfilmentStoreId?: number,
		mhlsPickupAddressId?: number,
		stockLevel?: string
	): void {
		if (!mhlsPickupAddressId) {
			return;
		}
		this.loading = true;
		this.fulfilmentService
			.changeSelectedPickupStore(mhlsPickupAddressId)
			.pipe(first())
			.subscribe(
				() => {
					const hasStock =
						stockLevel === FulfilmentStoreStockLevelResponse.StockLevelEnum.InStock ||
						stockLevel === FulfilmentStoreStockLevelResponse.StockLevelEnum.LowStock;

					if (hasStock) {
						this.addToTrolley();
					}
					this.loading = false;
					const prevUrl = this.historyService.getPreviousNonModalUrlForOutlet(
						MODAL_ROUTES.PRODUCT_DETAILS_MODAL
					);
					const parsedUrl = this.router.parseUrl(prevUrl);
					parsedUrl.queryParams = {
						stockcode: parsedUrl.queryParams.stockcode,
						name: parsedUrl.queryParams.name,
						store: physicalStoreId,
					};
					const url = this.router.serializeUrl(parsedUrl);
					this.close({ url });
				},
				() => {
					this.close();
				}
			);
	}

	onClose(): void {
		this.dataLayerService.trackEvent(
			TrackingEvent.NotificationEvent,
			NotificationType.Modal,
			'stock availability close',
			'stock availability'
		);
		this.close();
	}

	getStoreAccordionAnalyticsId(storeAccordion: AccordionItemComponent): string {
		const actionName = storeAccordion.isOpened ? 'collapsed' : 'expanded';
		return `stock availability store ${actionName}`;
	}
}
